import { saveWithVersion } from './staleSave'
import { reloadPage } from '@/lib/player/leave'

/**
 * The settings pages' version (`settingsUpdatedAt` of GET /api/communities/settings,
 * David 2026-10-28): the settings screen reads it once, every write of the
 * community's settings (PUT /communities/{id}, PATCH /communities/{id}/settings)
 * sends it and keeps the one its answer brings, and a stale write (409) asks
 * between reloading the page and saving over it. Unknown (a write outside the
 * settings screen): the write goes without the check, as before.
 */
let version: string | null = null
const listeners = new Set<() => void>()

const set = (next: string | null) => {
  if (next === version) return
  version = next
  for (const listener of [...listeners]) listener()
}

export const getSettingsVersion = (): string | null => version

export const subscribeSettingsVersion = (listener: () => void) => {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Keeps the version a settings answer carries (`settingsUpdatedAt`). */
export const noteSettingsVersion = (body: unknown) => {
  const value = (body as { settingsUpdatedAt?: unknown } | null)?.settingsUpdatedAt
  if (typeof value === 'string') set(value)
}

export const forgetSettingsVersion = () => set(null)

/** A settings write with the version read; on a stale 409 the person reloads the page or saves over it. */
export const versionedSettingsWrite = async <T,>(write: (version: string | null | undefined) => Promise<T>): Promise<T> => {
  const result = await saveWithVersion(write, {
    version,
    field: 'settingsUpdatedAt',
    onReload: () => reloadPage(),
  })
  noteSettingsVersion(result)
  return result
}
